import React, { useState, useEffect, useRef } from 'react';
import { BookMarked, Printer, Download, Search, Users, Store } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';
const TODAY_DATE = new Date().toISOString().split('T')[0];

function PartyLedgerPage() {
  const toast = useToast();
  const [ledgerType, setLedgerType] = useState('customer'); // 'customer' | 'vendor'

  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [dairyFarms, setDairyFarms] = useState([]);

  const [ledger, setLedger] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null); // customer or vendor object
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unbilledNote, setUnbilledNote] = useState(null); // { count, total } — unbilled sales not yet on this ledger

  const [filters, setFilters] = useState({ party_id: '', start_date: '', end_date: '' });

  // Dairy-farm scoping (customer ledger only): '' = All Dairy Farms, 'unassigned' = no farm, else a farm_id
  const [selectedFarmId, setSelectedFarmId] = useState('');
  const [farmOverview, setFarmOverview] = useState(null);
  const [loadingFarmOverview, setLoadingFarmOverview] = useState(false);
  const [farmAggregateView, setFarmAggregateView] = useState(false);

  const ledgerRef = useRef(null);

  // Load customers + vendors + dairy farms on mount, plus the default "All Dairy Farms" aggregate
  useEffect(() => {
    apiService.getCustomers().then(r => setCustomers(r.data || [])).catch(() => {});
    apiService.getVendors().then(r => setVendors(r.data || [])).catch(() => {});
    apiService.getDairyFarms().then(r => setDairyFarms(r.data || [])).catch(() => {});
    handleFarmChange('');
  }, []);

  // Reset when ledger type changes
  const handleTypeChange = (type) => {
    setLedgerType(type);
    setLedger(null);
    setSelectedParty(null);
    setFilters(f => ({ ...f, party_id: '' }));
    setError(null);
    setSelectedFarmId('');
    setFarmOverview(null);
    setFarmAggregateView(false);
  };

  // When the dairy-farm filter changes, load that farm's overview (used both to
  // narrow the customer dropdown and to render the "All Customers" aggregate view).
  // '' = All Dairy Farms (business-wide aggregate), 'unassigned' = no farm, else a farm_id.
  const handleFarmChange = async (value) => {
    setSelectedFarmId(value);
    setFilters(f => ({ ...f, party_id: '' }));
    setLedger(null);
    setFarmAggregateView(false);
    setFarmOverview(null);
    try {
      setLoadingFarmOverview(true);
      const res = value === 'unassigned'
        ? await apiService.getUnassignedFarmOverview()
        : value === ''
          ? await apiService.getAllFarmsOverview()
          : await apiService.getDairyFarmOverview(parseInt(value));
      setFarmOverview(res.data);
    } catch (err) {
      toast.error('Failed to load dairy farm data: ' + err.message);
    } finally {
      setLoadingFarmOverview(false);
    }
  };

  const handleFetch = async () => {
    // "All Customers" (with or without a specific dairy farm) → show the aggregate instead of a single ledger
    if (ledgerType === 'customer' && !filters.party_id) {
      if (!farmOverview) { toast.warning('Still loading data — please wait.'); return; }
      setFarmAggregateView(true);
      setLedger(null);
      return;
    }

    if (!filters.party_id) { toast.warning(`Please select a ${ledgerType}.`); return; }

    try {
      setLoading(true);
      setError(null);
      setUnbilledNote(null);
      setFarmAggregateView(false);
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      let response;
      if (ledgerType === 'customer') {
        const id = parseInt(filters.party_id);
        setSelectedParty(customers.find(c => c.customer_id === id) || null);
        if (selectedFarmId === 'unassigned') params.unassigned_farm = true;
        else if (selectedFarmId) params.dairy_farm_id = parseInt(selectedFarmId);
        response = await apiService.getCustomerLedger(id, params);
        apiService.getUnbilledBills(id)
          .then(r => setUnbilledNote({ count: r.count ?? (r.data || []).length, total: r.total_amount || 0 }))
          .catch(() => setUnbilledNote(null));
      } else {
        const id = parseInt(filters.party_id);
        setSelectedParty(vendors.find(v => v.vendor_id === id) || null);
        response = await apiService.getVendorLedger(id, params);
      }
      setLedger(response);
    } catch (err) {
      toast.error('Failed to load ledger: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '-';
  const formatCurrency = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;

  const printLedger = () => window.print();
  const downloadPDF = () => {
    const partyName = selectedParty?.name || 'Party';
    html2pdf().set({
      margin: 0.5,
      filename: `Ledger_${partyName}_${filters.start_date || 'all'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, ignoreElements: (el) => el.classList && el.classList.contains('print-hide') },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    }).from(ledgerRef.current).save();
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">{content}</div>
      </div>
    </div>
  );

  const isCustomer = ledgerType === 'customer';
  const partyList = isCustomer ? customers : vendors;
  const partyIdKey = isCustomer ? 'customer_id' : 'vendor_id';

  // For vendor ledger, debit label changes meaning
  const debitLabel = isCustomer ? 'Debit (Billed)' : 'Debit (Purchased)';
  const creditLabel = isCustomer ? 'Credit (Received)' : 'Credit (Paid)';

  return renderLayout(
    <>
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="page-header print-hide">
        <h1 className="page-title">
          <BookMarked size={24} style={{ marginRight: '0.5rem' }} /> Party Ledger
        </h1>
        {(ledger || farmAggregateView) && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={printLedger}>
              <Printer size={18} /> Print
            </button>
            {ledger && (
              <button className="btn btn-primary" onClick={downloadPDF}>
                <Download size={18} /> Download PDF
              </button>
            )}
          </div>
        )}
      </div>

      {error && <div className="error-container"><p>{error}</p></div>}

      {/* ── Filter card ─────────────────────────────────────────────────── */}
      <div className="card print-hide" style={{ marginBottom: '1.5rem' }}>

        {/* Ledger type toggle */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.2rem' }}>
          <button
            className={`btn ${isCustomer ? 'btn-primary' : 'btn-secondary'}`}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            onClick={() => handleTypeChange('customer')}
          >
            <Users size={16} /> Customer Ledger
          </button>
          <button
            className={`btn ${!isCustomer ? 'btn-primary' : 'btn-secondary'}`}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            onClick={() => handleTypeChange('vendor')}
          >
            <Store size={16} /> Vendor Ledger
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', alignItems: 'flex-end' }}>
          {isCustomer && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Dairy Farm</label>
              <select
                className="form-select"
                value={selectedFarmId}
                onChange={e => handleFarmChange(e.target.value)}
                disabled={loadingFarmOverview}
              >
                <option value="">All Dairy Farms</option>
                <option value="unassigned">Unassigned</option>
                {sortedByName(dairyFarms).map(f => (
                  <option key={f.farm_id} value={f.farm_id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{isCustomer ? 'Customer' : 'Vendor'} {!isCustomer && '*'}</label>
            <select
              className="form-select"
              value={filters.party_id}
              onChange={e => setFilters(p => ({ ...p, party_id: e.target.value }))}
            >
              {isCustomer ? (
                <>
                  <option value="">All Customers</option>
                  {sortedByName(selectedFarmId ? (farmOverview?.customers || []) : customers).map(c => (
                    <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                  ))}
                </>
              ) : (
                <>
                  <option value="">Select Vendor</option>
                  {sortedByName(partyList).map(p => (
                    <option key={p[partyIdKey]} value={p[partyIdKey]}>
                      {p.name} ({p.vendor_type})
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">From Date</label>
            <input
              type="date" max={TODAY_DATE} className="form-input" value={filters.start_date}
              onChange={e => setFilters(p => ({ ...p, start_date: e.target.value }))}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">To Date</label>
            <input
              type="date" max={TODAY_DATE} className="form-input" value={filters.end_date}
              onChange={e => setFilters(p => ({ ...p, end_date: e.target.value }))}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              className="btn btn-primary"
              style={{ width: '100%', height: '42px' }}
              onClick={handleFetch}
              disabled={loading || (!isCustomer && !filters.party_id)}
            >
              <Search size={18} /> {loading ? 'Loading...' : 'View Ledger'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Empty state ─────────────────────────────────────────────────── */}
      {!ledger && !farmAggregateView && !loading && (
        <div className="no-data" style={{ marginTop: '3rem' }}>
          <BookMarked size={40} style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }} />
          <p>Select a {isCustomer ? 'customer' : 'vendor'} and date range, then click <strong>View Ledger</strong>.</p>
        </div>
      )}

      {/* ── Dairy-farm aggregate view ("All Customers" under a farm) ──────── */}
      {farmAggregateView && farmOverview && (
        <>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{farmOverview.farm?.name}</div>
            <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
              All Customers Overview
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              ['Bills (invoiced)', farmOverview.totals.bill_count],
              ['Total Sales', formatCurrency(farmOverview.totals.total_sales)],
              ['Invoices', farmOverview.totals.invoice_count],
              ['Total Invoiced', formatCurrency(farmOverview.totals.total_invoiced)],
              ['Total Paid', formatCurrency(farmOverview.totals.total_paid)],
              ['Total Pending', formatCurrency(farmOverview.totals.total_pending)],
            ].map(([label, value]) => (
              <div key={label} className="card" style={{ padding: '1rem' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{label}</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{value}</div>
              </div>
            ))}
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th style={{ textAlign: 'right' }}>Total Billed</th>
                  <th style={{ textAlign: 'right' }}>Total Paid</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {farmOverview.customers.map(c => (
                  <tr key={c.customer_id}>
                    <td style={{ fontWeight: 500 }}>{c.name}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(c.total_billed)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(c.total_paid)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: c.balance > 0 ? 'var(--error-color)' : 'var(--success-color)' }}>
                      {formatCurrency(c.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {farmOverview.customers.length === 0 && <div className="no-data"><p>No customers under this farm yet</p></div>}
          </div>
        </>
      )}

      {/* ── Ledger output ───────────────────────────────────────────────── */}
      {ledger && (
        <>
          {isCustomer && unbilledNote && unbilledNote.count > 0 && (
            <div style={{
              background: 'var(--warning-soft, #fef3c7)', border: '1px solid var(--warning-color, #f59e0b)',
              borderRadius: '8px', padding: '0.8rem 1rem', marginBottom: '1rem', fontSize: '0.9rem'
            }}>
              ⚠️ <strong>{unbilledNote.count}</strong> unbilled sale record(s) worth <strong>{formatCurrency(unbilledNote.total)}</strong> for this customer are not yet reflected on this ledger (not invoiced yet).
            </div>
          )}
        <div ref={ledgerRef}>
          {/* Header (printed) */}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            {isCustomer && farmOverview?.farm?.name && (
              <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{farmOverview.farm.name}</div>
            )}
            <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
              {isCustomer ? 'Customer' : 'Vendor'} Ledger Account
            </div>
            {(filters.start_date || filters.end_date) && (
              <div style={{ fontSize: '0.9rem', marginTop: '0.3rem' }}>
                Period: {filters.start_date ? formatDate(filters.start_date) : 'Beginning'} &mdash; {filters.end_date ? formatDate(filters.end_date) : 'Today'}
              </div>
            )}
          </div>

          {/* Party summary bar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            background: 'var(--bg-secondary)', padding: '1rem 1.2rem',
            borderRadius: 'var(--radius-md)', marginBottom: '1.2rem',
            border: '1px solid var(--border-color)'
          }}>
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {isCustomer ? 'Customer' : 'Vendor'}
              </div>
              <div style={{ fontSize: '1.15rem', fontWeight: 700 }}>{selectedParty?.name}</div>
              {!isCustomer && selectedParty?.vendor_type && (
                <div style={{ fontSize: '0.85rem', color: 'var(--primary-color)', fontWeight: 600, marginTop: '0.2rem', textTransform: 'capitalize' }}>
                  {selectedParty.vendor_type} Vendor
                </div>
              )}
              {selectedParty?.address && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                  {selectedParty.address}
                </div>
              )}
              {!isCustomer && selectedParty?.phone && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  {selectedParty.phone}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Opening Balance</div>
              <div style={{
                fontSize: '1.1rem', fontWeight: 700,
                color: (ledger.opening_balance || 0) > 0 ? 'var(--error-color)' : 'var(--success-color)'
              }}>
                {formatCurrency(Math.abs(ledger.opening_balance || 0))}
                {(ledger.opening_balance || 0) !== 0 && (
                  <span style={{ fontSize: '0.8rem', marginLeft: '4px' }}>
                    {/* For customers: Dr means they owe us. For vendors: Dr means we owe them */}
                    {ledger.opening_balance > 0 ? 'Dr' : 'Cr'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Ledger table */}
          <div className="table-container">
            <table className="data-table" style={{ fontSize: '0.9rem' }}>
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Date</th>
                  <th>Particulars</th>
                  <th style={{ width: '100px' }}>Ref No</th>
                  <th style={{ textAlign: 'right', width: '110px' }}>{debitLabel}</th>
                  <th style={{ textAlign: 'right', width: '110px' }}>{creditLabel}</th>
                  <th style={{ textAlign: 'right', width: '120px' }}>Balance (₹)</th>
                </tr>
              </thead>
              <tbody>
                {/* Opening balance row */}
                {(ledger.opening_balance || 0) !== 0 && (
                  <tr style={{ background: '#fafafa', fontStyle: 'italic' }}>
                    <td style={{ color: 'var(--text-secondary)' }}>
                      {filters.start_date ? formatDate(filters.start_date) : '-'}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>Opening Balance</td>
                    <td>-</td>
                    <td style={{ textAlign: 'right' }}>
                      {ledger.opening_balance > 0 ? formatCurrency(ledger.opening_balance) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {ledger.opening_balance < 0 ? formatCurrency(Math.abs(ledger.opening_balance)) : '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {formatCurrency(Math.abs(ledger.opening_balance))}
                      <span style={{ fontSize: '0.75rem', marginLeft: '3px' }}>
                        {ledger.opening_balance > 0 ? 'Dr' : 'Cr'}
                      </span>
                    </td>
                  </tr>
                )}

                {(ledger.entries || []).length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      No transactions in this period.
                    </td>
                  </tr>
                )}

                {(ledger.entries || []).map((entry, idx) => {
                  const isPurchase = entry.type === 'milk_purchase' || entry.type === 'goods_purchase';
                  const isInvoice = entry.type === 'invoice';
                  const isDebit = entry.debit > 0;
                  const balance = entry.balance || 0;

                  // Row background: debit rows (bills/purchases) warm tint, credit rows (payments) cool tint
                  const rowBg = isDebit ? '#fff8f0' : '#f0fff4';

                  return (
                    <tr key={idx} style={{ background: rowBg }}>
                      <td>{formatDate(entry.date)}</td>
                      <td>
                        <div style={{ fontWeight: isDebit ? 600 : 400 }}>{entry.particulars}</div>
                        {/* Customer invoice: show period */}
                        {isInvoice && entry.period_start && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Period: {formatDate(entry.period_start)} – {formatDate(entry.period_end)}
                            {entry.due_date && ` | Due: ${formatDate(entry.due_date)}`}
                          </div>
                        )}
                        {/* Vendor purchase: show detail */}
                        {isPurchase && entry.detail && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {entry.detail}
                          </div>
                        )}
                        {/* Payment transaction id */}
                        {entry.transaction_id && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Txn: {entry.transaction_id}
                          </div>
                        )}
                        {entry.notes && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            {entry.notes}
                          </div>
                        )}
                      </td>
                      <td>
                        {isInvoice
                          ? <span style={{ fontWeight: 600, color: 'var(--primary-color)' }}>{entry.invoice_number}</span>
                          : entry.ref
                            ? <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{entry.ref}</span>
                            : entry.invoice_number
                              ? <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Ref: {entry.invoice_number}</span>
                              : '-'
                        }
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--error-color)', fontWeight: isDebit ? 600 : 400 }}>
                        {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--success-color)', fontWeight: !isDebit ? 600 : 400 }}>
                        {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(Math.abs(balance))}
                        {balance !== 0 && (
                          <span style={{ fontSize: '0.75rem', marginLeft: '3px', color: balance > 0 ? 'var(--error-color)' : 'var(--success-color)' }}>
                            {balance > 0 ? 'Dr' : 'Cr'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {(ledger.entries || []).length > 0 && (
                <tfoot>
                  <tr style={{ background: 'var(--bg-secondary)', fontWeight: 700 }}>
                    <td colSpan={3} style={{ textAlign: 'right', paddingRight: '1rem' }}>Totals</td>
                    <td style={{ textAlign: 'right', color: 'var(--error-color)' }}>
                      {formatCurrency((ledger.entries || []).reduce((s, e) => s + (e.debit || 0), 0))}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--success-color)' }}>
                      {formatCurrency((ledger.entries || []).reduce((s, e) => s + (e.credit || 0), 0))}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(Math.abs(ledger.closing_balance || 0))}
                      {(ledger.closing_balance || 0) !== 0 && (
                        <span style={{ fontSize: '0.75rem', marginLeft: '3px', color: (ledger.closing_balance || 0) > 0 ? 'var(--error-color)' : 'var(--success-color)' }}>
                          {(ledger.closing_balance || 0) > 0 ? 'Dr' : 'Cr'}
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Closing balance summary */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', marginTop: '1rem',
            padding: '1rem 1.5rem',
            background: (ledger.closing_balance || 0) > 0 ? '#fff5f5' : '#f0fff4',
            border: `1px solid ${(ledger.closing_balance || 0) > 0 ? '#fca5a5' : '#86efac'}`,
            borderRadius: 'var(--radius-md)'
          }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Closing Balance
              </div>
              <div style={{
                fontSize: '1.3rem', fontWeight: 700,
                color: (ledger.closing_balance || 0) > 0 ? 'var(--error-color)' : 'var(--success-color)'
              }}>
                {formatCurrency(Math.abs(ledger.closing_balance || 0))}
                {(ledger.closing_balance || 0) > 0
                  ? <span style={{ fontSize: '0.85rem', marginLeft: '6px' }}>
                      {isCustomer ? 'Receivable (Dr)' : 'Payable (Dr)'}
                    </span>
                  : (ledger.closing_balance || 0) < 0
                  ? <span style={{ fontSize: '0.85rem', marginLeft: '6px' }}>
                      {isCustomer ? 'Advance (Cr)' : 'Advance Paid (Cr)'}
                    </span>
                  : <span style={{ fontSize: '0.85rem', marginLeft: '6px' }}>Settled</span>
                }
              </div>
            </div>
          </div>
        </div>
        </>
      )}
    </>
  );
}

export default PartyLedgerPage;
